/**
 * My Money Meetings (TMMAPP-0159 §B: Meetings & decisions, promoted from Money Tools to the main
 * menu; TMMAPP-0103, audit A4 §1, P:1501-1540; v2, TMMAPP-0151 §I, prototype state 43): every
 * meeting the viewer is in, across every virtual account (`GET /v1/me/meetings`), each row naming
 * its virtual account. Its `h1` is the nav label, with no Money Tools eyebrow. The v2 view (`MeetingsOverview`: the next
 * meeting bar and the four cards) is the top of the page; the tabs follow it. Tabs Upcoming and Past
 * are one list card of `MeetingRow`s each; the D1 extras, the month Calendar and the open Items,
 * follow them. The tab is in the URL. A row's title opens the meeting editor (read-only for a viewer
 * who can't write); "+ New meeting" opens it with a virtual account picker of the viewer's own, or
 * Just us (TMMAPP-0159 §D 20). A standalone meeting's row reads "With {names}" where a virtual
 * account's names it, and opens `/me/meetings/{mid}/{occ}` (§D 24).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type MeetingCalendarEntry } from '@/api/meetings';
import { ErrorState } from '@/components/ErrorState';
import { AddIcon } from '@/components/icons';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { useSubscriberAction, SubscriberActionHint } from '@/session/useSubscriberAction';
import { enumParam, useUrlState } from '@/shared/useUrlState';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { resolveTokens } from '@/theme/theme';
import { MeetingRow } from './MeetingRow';
import { MeetingsCalendarTab } from './MeetingsCalendarTab';
import { MeetingsItemsTab } from './MeetingsItemsTab';
import { MeetingsOverview } from './MeetingsOverview';
import { nowMs, pastRange, splitMeetings, upcomingRange } from './meetingLists';
import { entryWithLabel } from './standalone';
import { useMeetingActions } from './useMeetingActions';

const TABS = ['upcoming', 'past', 'calendar', 'items'] as const;
type MeetingsTabKey = (typeof TABS)[number];
const URL_SCHEMA = { tab: enumParam<MeetingsTabKey>(TABS, 'upcoming') };

export function MeetingsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const today = todayCivilInZone(zone);
  const title = usePageTitle({ title: t('nav.meetings') });
  const [{ tab }, setUrl] = useUrlState(URL_SCHEMA);
  const [entries, setEntries] = useState<MeetingCalendarEntry[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  // One refresh for the page's items (TMMAPP-0154 item 18): a tick in the cards reloads the Items tab,
  // and a change in the Items tab reloads the cards.
  const [itemsTick, setItemsTick] = useState(0);
  const itemsChanged = () => setItemsTick((n) => n + 1);
  // The dashboard picker (TMMAPP-0110): the virtual accounts the viewer owns or manages.
  const writable = me.virtualAccounts.filter((row) => row.canManage);

  const load = useCallback(async () => {
    try {
      const [ahead, behind] = await Promise.all([
        meetingsApi.listMine(upcomingRange(today)),
        meetingsApi.listMine(pastRange(today)),
      ]);
      setEntries([...ahead.items, ...behind.items]);
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [today, setEntries, setLoadFailure]);

  const meetings = useMeetingActions({
    writer: (entry) =>
      me.virtualAccounts.find((row) => row.id === entry.virtualAccountId)?.canManage ?? false,
    reload: load,
  });
  // A Manager on someone else's virtual account creates meetings there at any tier (MM-D-115), and
  // may schedule a Just us meeting with its people (§D 23); a Read-only guest gets the purchase.
  const newMeeting = useSubscriberAction(
    () =>
      meetings.openNew({
        choices: writable.map((row) => ({ id: row.id, name: row.name })),
        standalone: true,
      }),
    writable.some((row) => !row.isOwner),
  );

  const lastFetch = useRef<string | null>(null);
  useEffect(() => {
    if (lastFetch.current === today) return;
    lastFetch.current = today;
    void load();
  }, [today, load]);

  const now = nowMs();
  const { upcoming, past } = splitMeetings(entries ?? [], now);

  // A standalone meeting is labelled by its people, where a virtual account's names it (§D 24).
  const placeName = (entry: MeetingCalendarEntry) =>
    entry.virtualAccountId === null
      ? entryWithLabel(entry, me.user.id, t, locale)
      : me.virtualAccounts.find((row) => row.id === entry.virtualAccountId)?.name;

  const header = (
    <PageHeader
      title={title}
      subtitle={
        <Box component="span" sx={{ display: 'block', maxWidth: 620 }}>
          {t('meetings.pageSubtitle')}
        </Box>
      }
      actions={
        <>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            aria-describedby={newMeeting.descriptionId}
            onClick={newMeeting.run}
          >
            {t('meetings.new')}
          </Button>
          <SubscriberActionHint
            id={newMeeting.descriptionId}
            description={newMeeting.description}
          />
        </>
      }
    />
  );

  function list(rows: MeetingCalendarEntry[], empty: string) {
    if (entries === null) return <Typography>{t('common.loading')}</Typography>;
    return (
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        {rows.length === 0 ? (
          <Typography sx={{ p: '20px', fontSize: 14, color: tokens.text2 }}>{empty}</Typography>
        ) : (
          rows.map((entry, index) => (
            <MeetingRow
              key={`${entry.meetingId}-${entry.occurrenceKey}`}
              entry={entry}
              virtualAccountName={placeName(entry)}
              onOpen={() => void meetings.openEditor(entry)}
              actions={meetings.actionsFor(entry)}
              last={index === rows.length - 1}
            />
          ))
        )}
      </Paper>
    );
  }

  if (loadFailure !== null && (tab === 'upcoming' || tab === 'past')) {
    return (
      <>
        {header}
        <ErrorState
          error={loadFailure.error}
          what={t('errors.what.meetings')}
          onRetry={() => void load()}
        />
      </>
    );
  }

  return (
    <>
      {header}
      {entries !== null && (
        <MeetingsOverview
          upcoming={upcoming}
          past={past}
          reloadToken={itemsTick}
          onItemsChanged={itemsChanged}
        />
      )}
      <Tabs
        value={tab}
        onChange={(_event, next: MeetingsTabKey) => setUrl({ tab: next })}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab value="upcoming" label={t('meetings.tabUpcoming')} />
        <Tab value="past" label={t('meetings.tabPast')} />
        <Tab value="calendar" label={t('meetings.calendar')} />
        <Tab value="items" label={t('workspace.items')} />
      </Tabs>
      {tab === 'upcoming' &&
        list(
          upcoming,
          // The first-meeting prompt only when there are none at all (TMMAPP-0160 item 4).
          past.length === 0 ? t('meetings.emptyUpcoming') : t('meetings.nothingScheduled'),
        )}
      {tab === 'past' && list(past, t('meetings.emptyPast'))}
      {tab === 'calendar' && <MeetingsCalendarTab />}
      {tab === 'items' && <MeetingsItemsTab reloadToken={itemsTick} onChanged={itemsChanged} />}

      {meetings.node}
    </>
  );
}
